/**
 * WebSocket Client — Event-emitter based connection to the Brain Server.
 *
 * Usage:
 *   const ws = new WSClient(wsUrl, token)
 *   const off = ws.on('message', (data) => { ... })
 *   ws.connect()
 *   // later:
 *   off()          // unsubscribe
 *   ws.disconnect() // close connection
 */

type WSEvent = 'message' | 'open' | 'close' | 'error'
type Handler = (data?: any) => void

const RECONNECT_DELAY_MS = 3000

export class WSClient {
  private ws: WebSocket | null = null
  private listeners = new Map<WSEvent, Set<Handler>>()
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private intentionalClose = false
  private url: string
  private token: string

  constructor(url: string, token: string) {
    this.url = url
    this.token = token
  }

  /** Subscribe to an event. Returns an unsubscribe function. */
  on(event: WSEvent, handler: Handler): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(handler)
    return () => { this.listeners.get(event)?.delete(handler) }
  }

  /** Open the WebSocket connection. */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) return
    this.intentionalClose = false

    const fullUrl = `${this.url}?token=${encodeURIComponent(this.token)}`
    this.ws = new WebSocket(fullUrl)

    this.ws.onopen = () => {
      this.clearReconnect()
      this.emit('open')
    }

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        this.emit('message', data)
      } catch {
        // Non-JSON message — ignore
      }
    }

    this.ws.onerror = (event) => {
      this.emit('error', event)
    }

    this.ws.onclose = () => {
      this.emit('close')
      if (!this.intentionalClose) {
        this.scheduleReconnect()
      }
    }
  }

  /** Send a JSON message. */
  send(data: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data))
    }
  }

  /** Close the connection (no auto-reconnect). */
  disconnect(): void {
    this.intentionalClose = true
    this.clearReconnect()
    this.ws?.close()
    this.ws = null
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  private emit(event: WSEvent, data?: any): void {
    this.listeners.get(event)?.forEach((handler) => {
      try { handler(data) } catch (err) {
        console.error(`[ws-client] Handler error for ${event}:`, err)
      }
    })
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, RECONNECT_DELAY_MS)
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }
}
